import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Database, RefreshCw, Loader2, Search, Tag, Folder, Layers, Mail, AlertCircle, ChevronDown,
} from 'lucide-react';

const API = '/api'; // Same-origin: /api routes on the serving domain (Preview or Production custom domain) to backend via Emergent ingress. Avoids cross-domain CORS / tracking-prevention failures.

function PillStat({ label, value, accent = '#48B64C', testid }) {
  return (
    <div data-testid={testid} className="bg-hg-surface border border-hg-border rounded-sm p-4">
      <div className="text-[10px] font-sans text-hg-text-dim uppercase tracking-[0.2em]">{label}</div>
      <div className="font-display text-2xl text-hg-text tracking-wider mt-2" style={{ color: value === 0 ? '#888' : accent }}>{value.toLocaleString()}</div>
    </div>
  );
}

function FilterSection({ icon: Icon, label, items, selected, onSelect, testid }) {
  const [expanded, setExpanded] = useState(true);
  const visible = expanded ? items : items.slice(0, 6);
  return (
    <div className="bg-hg-surface border border-hg-border rounded-sm p-4 space-y-2" data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-[10px] font-sans text-hg-text-dim uppercase tracking-[0.2em]">
          <Icon size={11} strokeWidth={1.5} /> {label}
        </div>
        <span className="text-[10px] text-hg-text-dim font-sans">{items.length}</span>
      </div>
      {items.length === 0 ? (
        <div className="text-[11px] text-hg-text-dim font-sans py-2">None</div>
      ) : (
        <>
          <div className="space-y-1">
            <button
              onClick={() => onSelect(null)}
              className={`w-full text-left px-2 py-1.5 rounded-sm text-[11px] font-sans transition-colors flex items-center justify-between ${
                !selected ? 'bg-hg-green/10 text-hg-green' : 'text-hg-text-dim hover:bg-hg-bg'
              }`}
            >
              <span>All</span>
            </button>
            {visible.map((it) => (
              <button
                key={it.name}
                onClick={() => onSelect(it.name)}
                data-testid={`${testid}-item-${it.name.replace(/[^a-z0-9]/gi,'_').toLowerCase()}`}
                className={`w-full text-left px-2 py-1.5 rounded-sm text-[11px] font-sans transition-colors flex items-center justify-between gap-2 ${
                  selected === it.name ? 'bg-hg-green/10 text-hg-green' : 'text-hg-text-dim hover:bg-hg-bg'
                }`}
              >
                <span className="truncate">{it.name}</span>
                <span className="flex-shrink-0 text-[10px] opacity-70">{it.count.toLocaleString()}</span>
              </button>
            ))}
          </div>
          {items.length > 6 && (
            <button
              onClick={() => setExpanded(!expanded)}
              className="w-full text-[10px] text-hg-green hover:underline text-center pt-1 font-sans flex items-center justify-center gap-1"
            >
              {expanded ? 'Show fewer' : `Show all ${items.length}`}
              <ChevronDown size={10} className={expanded ? 'rotate-180 transition-transform' : 'transition-transform'} />
            </button>
          )}
        </>
      )}
    </div>
  );
}

export default function MailchimpLibrary() {
  const [stats, setStats] = useState(null);
  const [audiences, setAudiences] = useState([]);
  const [subs, setSubs] = useState([]);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState({ audience: null, tag: null, interest_group: null });
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState('');
  const [error, setError] = useState('');

  const loadStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/mailchimp/subscriber-stats`);
      setStats(res.data);
    } catch (e) { setError(e.response?.data?.detail || 'Failed to load stats'); }
  }, []);

  const loadAudiences = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/mailchimp/audiences`);
      setAudiences(res.data.audiences || []);
    } catch (e) { /* non-fatal */ }
  }, []);

  const loadSubs = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = { page, per_page: 50 };
      if (filters.audience) params.audience = filters.audience;
      if (filters.tag) params.tag = filters.tag;
      if (filters.interest_group) params.interest_group = filters.interest_group;
      if (search) params.search = search;
      const res = await axios.get(`${API}/mailchimp/subscribers`, { params });
      setSubs(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load subscribers');
    }
    setLoading(false);
  }, [filters, search, page]);

  useEffect(() => { loadStats(); loadAudiences(); }, [loadStats, loadAudiences]);
  useEffect(() => { loadSubs(); }, [loadSubs]);

  const handleSync = async () => {
    if (audiences.length === 0) {
      setError('No Mailchimp audiences found. Check API key in Settings.');
      return;
    }
    setSyncing(true);
    setSyncMsg('Starting sync…');
    setError('');
    let totalSynced = 0;
    try {
      for (let i = 0; i < audiences.length; i++) {
        const aud = audiences[i];
        setSyncMsg(`Syncing ${aud.name} (${i+1}/${audiences.length})… ${aud.member_count.toLocaleString()} members`);
        const res = await axios.post(`${API}/mailchimp/sync-subscribers`, null, { params: { audience_id: aud.id } });
        totalSynced += res.data.total_members_seen || 0;
      }
      setSyncMsg(`Sync complete. ${totalSynced.toLocaleString()} member records processed.`);
      await loadStats();
      await loadSubs();
    } catch (e) {
      setError(e.response?.data?.detail || 'Sync failed');
    }
    setSyncing(false);
    setTimeout(() => setSyncMsg(''), 8000);
  };

  const setFilter = (key, value) => {
    setFilters((f) => ({ ...f, [key]: value }));
    setPage(1);
  };

  const totalPages = Math.max(1, Math.ceil(total / 50));
  const hasFilters = filters.audience || filters.tag || filters.interest_group || search;

  return (
    <div className="space-y-5" data-testid="mailchimp-library">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <Database size={14} className="text-hg-green" strokeWidth={1.5} />
            <h2 className="font-display text-xl text-hg-text tracking-wider">MAILCHIMP LIBRARY</h2>
          </div>
          <p className="text-xs text-hg-text-dim font-sans mt-1">All historical contacts pulled from Mailchimp — categorized by audience, tag, and interest group. Read-only reference.</p>
        </div>
        <button
          data-testid="mc-sync-btn"
          onClick={handleSync}
          disabled={syncing}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-sans border border-hg-green/40 bg-hg-green/5 text-hg-green rounded-sm hover:bg-hg-green/10 transition-colors disabled:opacity-40"
        >
          {syncing ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
          {syncing ? 'Syncing…' : 'Re-sync from Mailchimp'}
        </button>
      </div>

      {syncMsg && (
        <div data-testid="mc-sync-msg" className="bg-hg-green/10 border border-hg-green/30 rounded-sm p-3 text-xs text-hg-green font-sans flex items-center gap-2">
          {syncing && <Loader2 size={12} className="animate-spin flex-shrink-0" />}
          {syncMsg}
        </div>
      )}

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-sm p-3 flex items-start gap-2">
          <AlertCircle size={14} className="text-red-400 mt-0.5 flex-shrink-0" />
          <span className="text-xs text-red-400 font-sans">{error}</span>
        </div>
      )}

      {/* Top stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <PillStat label="Total in Library" value={stats.total_subscribers || 0} testid="mc-stat-total" />
          <PillStat label="Active (Subscribed)" value={stats.active_subscribers || 0} testid="mc-stat-active" />
          <PillStat label="Audiences" value={stats.by_audience.length} testid="mc-stat-audiences" />
          <PillStat label="Unique Tags" value={stats.by_tag.length} testid="mc-stat-tags" />
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-4">
        {/* Sidebar filters */}
        <aside className="space-y-3">
          {stats && (
            <>
              <FilterSection icon={Folder} label="Audiences" items={stats.by_audience} selected={filters.audience} onSelect={(v) => setFilter('audience', v)} testid="mc-filter-audience" />
              <FilterSection icon={Tag} label="Tags" items={stats.by_tag} selected={filters.tag} onSelect={(v) => setFilter('tag', v)} testid="mc-filter-tag" />
              <FilterSection icon={Layers} label="Interest Groups" items={stats.by_interest_group} selected={filters.interest_group} onSelect={(v) => setFilter('interest_group', v)} testid="mc-filter-group" />
            </>
          )}
        </aside>

        {/* Main results */}
        <div className="space-y-3">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative flex-1 min-w-[200px]">
              <Search size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-hg-text-dim" />
              <input
                data-testid="mc-search"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                placeholder="Search email or name…"
                className="w-full bg-hg-bg border border-hg-border rounded-sm pl-8 pr-3 py-2 text-xs text-hg-text font-sans focus:outline-none focus:ring-1 focus:ring-hg-green"
              />
            </div>
            {hasFilters && (
            <button
              onClick={() => { setFilters({ audience: null, tag: null, interest_group: null }); setSearch(''); setPage(1); }}
              className="px-3 py-2 text-[11px] font-sans text-hg-text-dim border border-hg-border rounded-sm hover:border-hg-green hover:text-hg-green transition-colors"
              data-testid="mc-clear-filters"
            >
              Clear filters
            </button>
          )}
          {(filters.tag || filters.audience) && (
            <button
              data-testid="mc-push-to-mailerlite"
              onClick={async () => {
                const ft = filters.tag ? 'mailchimp_tag' : 'mailchimp_audience';
                const fv = filters.tag || filters.audience;
                if (!confirm(`Push all subscribed members of "${fv}" into a Mailerlite group?`)) return;
                try {
                  const res = await axios.post(`${API}/people/sync-from-mailchimp`, {
                    mailchimp_filter_type: ft,
                    mailchimp_filter_value: fv,
                    new_group_name: fv,
                  });
                  alert(`Pushed ${res.data.pushed} of ${res.data.total} into Mailerlite group.`);
                } catch (e) {
                  alert(e.response?.data?.detail || 'Push failed');
                }
              }}
              className="px-3 py-2 text-[11px] font-sans bg-hg-green text-white rounded-sm hover:bg-hg-green-hover transition-colors flex items-center gap-1.5"
            >
              <Mail size={11} /> Push to Mailerlite
            </button>
          )}
          </div>

          {/* Active filter chips */}
          {(filters.audience || filters.tag || filters.interest_group) && (
            <div className="flex flex-wrap gap-1.5">
              {filters.audience && <Chip label={`Audience: ${filters.audience}`} onClear={() => setFilter('audience', null)} />}
              {filters.tag && <Chip label={`Tag: ${filters.tag}`} onClear={() => setFilter('tag', null)} />}
              {filters.interest_group && <Chip label={`Group: ${filters.interest_group}`} onClear={() => setFilter('interest_group', null)} />}
            </div>
          )}

          <div className="bg-hg-surface border border-hg-border rounded-sm">
            <div className="flex items-center justify-between px-5 py-3 border-b border-hg-border">
              <h3 className="text-xs font-sans text-hg-text-dim uppercase tracking-[0.2em]">Subscribers</h3>
              <span className="text-[10px] text-hg-text-dim font-sans">
                {loading ? '…' : `${total.toLocaleString()} match${total === 1 ? '' : 'es'} · page ${page}/${totalPages}`}
              </span>
            </div>
            {loading ? (
              <div className="flex justify-center py-12"><Loader2 size={20} className="animate-spin text-hg-green" /></div>
            ) : subs.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 gap-2">
                <Mail size={20} className="text-hg-text-dim" strokeWidth={1.5} />
                <p className="text-sm text-hg-text-dim font-sans">
                  {(stats && stats.total_subscribers === 0)
                    ? 'No subscribers yet. Click "Re-sync from Mailchimp" to import.'
                    : 'No matches for these filters.'}
                </p>
              </div>
            ) : (
              <div data-testid="mc-subs-list">
                {subs.map((s) => (
                  <div key={s.id} className="border-b border-hg-border/40 last:border-b-0 px-5 py-3 hover:bg-hg-bg/40">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-sans text-hg-text truncate">{s.email}</span>
                          <span className={`text-[9px] px-1.5 py-0.5 rounded-sm uppercase tracking-wider flex-shrink-0 ${
                            s.status === 'subscribed' ? 'bg-hg-green/10 text-hg-green' :
                            s.status === 'unsubscribed' ? 'bg-red-500/10 text-red-400' :
                            'bg-hg-border/40 text-hg-text-dim'
                          }`}>{s.status}</span>
                          <span className="text-[9px] text-hg-text-dim font-sans">{s.audience_name}</span>
                        </div>
                        {s.full_name && <div className="text-[11px] text-hg-text-dim font-sans mt-0.5">{s.full_name}</div>}
                        <div className="flex flex-wrap gap-1 mt-1.5">
                          {(s.tags || []).map((t) => (
                            <span key={`t-${t}`} className="text-[9px] px-1.5 py-0.5 rounded-sm bg-hg-border/40 text-hg-text-dim">#{t}</span>
                          ))}
                          {(s.interest_groups || []).map((g) => (
                            <span key={`g-${g}`} className="text-[9px] px-1.5 py-0.5 rounded-sm bg-hg-green/10 text-hg-green">{g}</span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {totalPages > 1 && (
              <div className="flex items-center justify-between px-5 py-3 border-t border-hg-border text-[11px] font-sans">
                <button
                  data-testid="mc-prev-page"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  className="px-3 py-1 border border-hg-border rounded-sm text-hg-text-dim hover:border-hg-green hover:text-hg-green disabled:opacity-30"
                >Previous</button>
                <span className="text-hg-text-dim">Page {page} of {totalPages}</span>
                <button
                  data-testid="mc-next-page"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                  className="px-3 py-1 border border-hg-border rounded-sm text-hg-text-dim hover:border-hg-green hover:text-hg-green disabled:opacity-30"
                >Next</button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Chip({ label, onClear }) {
  return (
    <span className="inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded-sm bg-hg-green/10 text-hg-green font-sans">
      {label}
      <button onClick={onClear} className="hover:text-hg-text">×</button>
    </span>
  );
}
